import { RatingUnitId } from "./questionnaire";
import registry from "../../../config/report-analyst.json";

/**
 * Shared contracts for the experiment-scoped survey results analyst.
 *
 * These contracts describe grounded, descriptive analysis only. Numeric
 * evidence is directional, and respondent quotes always disclose their
 * synthetic provenance.
 */

export const ANALYST_EVIDENCE_POLICY = {
  minimumCellN: 1,
  numericInterpretation: "directional",
  quoteProvenance: "synthetic",
} as const;

export type AnalystGateAction = "allow" | "redirect" | "refuse" | "unavailable";

export type AnalystEvidenceSourceType =
  | "survey_manifest"
  | "survey_json"
  | "canonical_analysis"
  // The report's own writing (story, slide narratives, recommendations): what
  // the report says, never a result.
  | "report_prose"
  | "results"
  | "qualitative_summary"
  | "methodology"
  | "custom_analysis";

export type AnalystEvidenceFactKind = "numeric" | "quote" | "text" | "respondent";

export type AnalystAnswerability =
  | "answered"
  | "partial"
  | "missing_data"
  | "missing_capability"
  | "refused"
  | "tool_error";

export type AnalystChartType =
  | "bar"
  | "stacked_bar"
  | "line"
  | "area"
  | "donut"
  | "ranking"
  | "histogram";

export type AnalystMessageRole = "user" | "assistant" | "tool" | "system";

/**
 * The analyst tab's suggestion chips, by id. The browser sends the id of the
 * chip a message came from and the server stores it on the user message, so
 * the selected chip is read back from the id, never matched on its words
 * (which change with the UI language). The planner gives it no meaning.
 */
export const ANALYST_SUGGESTION_IDS = [
  "key_findings",
  "crosstab",
  "demographics",
] as const;

export type AnalystSuggestionId = (typeof ANALYST_SUGGESTION_IDS)[number];

/**
 * The analyst run vocabulary is `config/report-analyst.json`, read by Python
 * (`apps/python-api/models/report_analyst_config.py`) and by the DB enum
 * (`report_analyst_async_status`). A status is terminal when its entry says so;
 * every reader asks `isTerminalAnalystStatus`, never a list of its own.
 */
if (registry.schemaVersion !== "report-analyst.v1") {
  throw new TypeError("Unsupported report analyst registry.");
}

export type AnalystAsyncStatus = keyof typeof registry.runStatuses;

/** Every run and message status, in the registry's (and the DB enum's) order. */
export const ANALYST_RUN_STATUSES = Object.keys(
  registry.runStatuses,
) as ReadonlyArray<AnalystAsyncStatus>;

/**
 * How long Python works on one turn before it answers `failed` (its
 * `asyncio.wait_for`). Nest waits a little longer, so it reads that answer.
 */
export const ANALYST_TURN_BUDGET_SECONDS: number = registry.turnBudgetSeconds;

/** Whether a run or message status is final: the turn has its outcome. */
export function isTerminalAnalystStatus(status: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(registry.runStatuses, status) &&
    registry.runStatuses[status as AnalystAsyncStatus].terminal
  );
}

/** The statuses of a run still in flight (claimable, blocking a new send). */
export const ANALYST_ACTIVE_STATUSES: ReadonlyArray<AnalystAsyncStatus> =
  ANALYST_RUN_STATUSES.filter((status) => !isTerminalAnalystStatus(status));

/** A final status with an answer to show and to carry as history: not a failure. */
export function isAnsweredAnalystStatus(status: string): boolean {
  return isTerminalAnalystStatus(status) && status !== "failed";
}

/**
 * The kind of reply an assistant turn gave (`config/report-analyst.json`'s
 * `responseKinds`). Python derives it once from the turn's typed record; a
 * reader asks it, never the reply's wording.
 */
export type AnalystResponseKind = keyof typeof registry.responseKinds;

/** Every reply kind, in the registry's order. */
export const ANALYST_RESPONSE_KINDS = Object.keys(
  registry.responseKinds,
) as ReadonlyArray<AnalystResponseKind>;

/**
 * Why a failed turn failed (`config/report-analyst.json`'s `failureCodes`).
 * Python or Nest sets one on every failed turn; the browser shows its copy.
 */
export type AnalystFailureCode = keyof typeof registry.failureCodes;

/** Every failure code, in the registry's order. */
export const ANALYST_FAILURE_CODES = Object.keys(
  registry.failureCodes,
) as ReadonlyArray<AnalystFailureCode>;

/** The browser copy key (under `studyReport.analyst`) a failure code shows. */
export function analystFailureCopyKey(code: AnalystFailureCode): string {
  return registry.failureCodes[code].copyKey;
}

export interface AnalystEvidencePolicy {
  minimumCellN: number;
  numericInterpretation: "directional";
  quoteProvenance: "synthetic";
}

export interface SurveyManifestQuestion {
  id: string;
  title: string;
  type: string;
  answerOptions?: Array<string>;
  measureIds?: Array<string>;
}

export interface SurveyManifestMeasure {
  id: string;
  label: string;
  unit: string;
  questionIds: Array<string>;
  description?: string;
}

export interface SurveyManifestSegment {
  id: string;
  label: string;
  sourceColumn: string;
  values: Array<string>;
}

export interface SurveyManifestMethodology {
  populationDescription: string;
  sampleSize: number;
  fieldingMethod: string;
  limitations: Array<string>;
  weightingDescription?: string;
}

export interface SurveyManifestScreenOut {
  questionId: string;
  screenedOutCount: number;
}

export interface SurveyManifestDisposition {
  assigned: number;
  completed: number;
  screenedOut: number;
  qualified: number;
  screenOuts: Array<SurveyManifestScreenOut>;
  screenOutRuleQuestionIds: Array<string>;
}

export interface SurveyManifestLaneScope {
  laneQuestionNames: Array<string>;
  laneAssetIds: Array<string>;
}

export interface SurveyManifest {
  schemaVersion: "survey-manifest.v1";
  experimentToken: string;
  title: string;
  /** The one reply language: the report's. */
  language: string;
  questions: Array<SurveyManifestQuestion>;
  measures: Array<SurveyManifestMeasure>;
  segments: Array<SurveyManifestSegment>;
  methodology: SurveyManifestMethodology;
  disposition?: SurveyManifestDisposition | null;
  monadicLaneScope?: SurveyManifestLaneScope | null;
}

export interface GateDecision {
  action: AnalystGateAction;
  reasonCode: string;
}

export interface EvidenceSource {
  sourceId: string;
  sourceType: AnalystEvidenceSourceType;
  label: string;
  questionId?: string;
  questionIds?: Array<string>;
  measureId?: string;
  artifactVersion?: string;
}

export interface EvidenceFilter {
  field: string;
  operator: "eq" | "in";
  values: Array<string>;
}

export interface EvidenceBase {
  n: number;
  weightedN?: number;
  cellN?: number;
  filters: Array<EvidenceFilter>;
  isSuppressed: boolean;
  suppressionThreshold: number;
}

export interface EvidenceFactBase {
  factId: string;
  kind: AnalystEvidenceFactKind;
  label: string;
  source: EvidenceSource;
  warnings: Array<string>;
}

/**
 * How an analyst figure reads, typed by its producer: a share of 100 (printed
 * with "%"), a whole count, a mean, an index or score, or any other number.
 * The one owner of the Analyst's figure formatting; `unit` stays the figure's
 * display label and is never parsed. Mirrors Python's `AnalystValueFormat`
 * (models/report_analyst.py; parity-tested).
 */
export const ANALYST_VALUE_FORMATS = ["percent", "count", "mean", "index", "number"] as const;
export type AnalystValueFormat = (typeof ANALYST_VALUE_FORMATS)[number];

export interface NumericEvidenceFact extends EvidenceFactBase {
  kind: "numeric";
  value: number | null;
  unit: string;
  valueFormat: AnalystValueFormat;
  base: EvidenceBase;
  computation: string;
  interpretation: "directional";
}

export interface SyntheticQuoteProvenance {
  synthetic: true;
  sourceId: string;
  questionId: string;
  respondentToken?: string;
}

export interface QuoteEvidenceFact extends EvidenceFactBase {
  kind: "quote";
  quote: string;
  provenance: SyntheticQuoteProvenance;
}

export interface TextEvidenceFact extends EvidenceFactBase {
  kind: "text";
  text: string;
}

export interface RespondentStandoutAnswer {
  questionId: string;
  title: string;
  value: string;
  why: string;
}

export interface RespondentEvidenceFact extends EvidenceFactBase {
  kind: "respondent";
  respondentId: string;
  personaName?: string;
  attributes: Record<string, string>;
  score: number;
  criteria: string;
  rationale: string;
  standoutAnswers: Array<RespondentStandoutAnswer>;
}

export type EvidenceFact =
  | NumericEvidenceFact
  | QuoteEvidenceFact
  | TextEvidenceFact
  | RespondentEvidenceFact;

export interface AnalystChartDatum {
  category: string;
  value: number | null;
  baseN: number;
  isSuppressed: boolean;
  /** Untruncated option label when `category` was shortened; omit when identical. */
  categoryFull?: string;
  /** The numeric fact this datum draws (plots plan T); absent on a pad and on older payloads. */
  evidenceFactId?: string;
}

/**
 * A registry plot payload (`packages/slide-schemas/plots`) on an analyst chart
 * (plots plan T). `kind` is one of the registry's plot kinds; `data` validates
 * against that kind's schema in Python before it is stamped.
 */
export interface AnalystChartPlot {
  kind: string;
  data: Record<string, unknown>;
  alternatives?: Array<{ kind: string; data: Record<string, unknown> }>;
}

export interface AnalystChartSeries {
  id: string;
  label: string;
  unit: string;
  valueFormat: AnalystValueFormat;
  data: Array<AnalystChartDatum>;
  /** Typed rating unit of a mean series, stamped by the producer; absent for shares, counts and unscaled means. */
  scaleUnit?: RatingUnitId;
  /** The declared scale's ends when the categories are its points (quick-study rating distribution); both or neither. */
  scaleMin?: number;
  scaleMax?: number;
}

export interface AnalystChartSpec {
  schemaVersion: "analyst-chart.v1";
  chartType: AnalystChartType;
  title: string;
  subtitle?: string;
  series: Array<AnalystChartSeries>;
  evidenceFactIds: Array<string>;
  interpretation: "directional";
  /** The plot the analyst tab draws; `chartType` is then its alias. Absent on older payloads. */
  plot?: AnalystChartPlot;
}

export interface AnalystTableColumn {
  id: string;
  label: string;
  unit: string;
  valueFormat: AnalystValueFormat;
  axisValue?: string;
  metric?: "count" | "row_percent" | "column_percent" | "total_percent" | "mean";
  /** Typed rating unit of a mean column, stamped by the producer; absent for shares, counts and unscaled means. */
  scaleUnit?: RatingUnitId;
}

export interface AnalystTableAxis {
  kind: "question" | "audience_attribute" | "matrix_row" | "matrix_cell" | "per_asset";
  id: string;
  label: string;
  questionId?: string;
  rowValue?: string;
  columnName?: string;
}

export interface AnalystTableCell {
  columnId: string;
  value: number | null;
  baseN: number;
  isSuppressed: boolean;
  suppressionThreshold?: number;
  evidenceFactId: string;
}

export interface AnalystTableRow {
  id: string;
  label: string;
  cells: Array<AnalystTableCell>;
}

export interface AnalystTableSpec {
  schemaVersion: "analyst-table.v1";
  title: string;
  subtitle?: string;
  columns: Array<AnalystTableColumn>;
  rows: Array<AnalystTableRow>;
  evidenceFactIds: Array<string>;
  interpretation: "directional";
  tableKind?: "summary" | "distribution" | "segmented_breakdown" | "crosstab";
  rowAxis?: AnalystTableAxis;
  columnAxis?: AnalystTableAxis;
  metrics?: Array<"count" | "row_percent" | "column_percent" | "total_percent" | "mean">;
  percentageBase?: "row" | "column" | "total";
  weightingMode?: "unweighted" | "study_weight";
  isFallback?: boolean;
  /** A dense table's heat-grid plot, Python's (plots wave 6). Absent on other tables and older payloads. */
  plot?: AnalystChartPlot;
}

export interface TableFulfillment {
  status: "fulfilled" | "partial" | "missing_data" | "not_requested";
  requestedKind: "summary" | "distribution" | "segmented_breakdown" | "crosstab";
  reasonCode:
    | "fulfilled"
    | "unresolved_axis"
    | "unsupported_matrix_type"
    | "ambiguous_matrix_cell"
    | "no_respondent_overlap"
    | "invalid_prior_artifact"
    | "oversized_result"
    | "unavailable_weighting"
    | "incompatible_evidence";
  unresolvedAxes: Array<string>;
  unresolvedMetrics: Array<string>;
  fallbackTableIndexes: Array<number>;
}

export interface AnalysisToolResult {
  toolCallId: string;
  toolName: string;
  answerability: AnalystAnswerability;
  facts: Array<EvidenceFact>;
  charts: Array<AnalystChartSpec>;
  tables: Array<AnalystTableSpec>;
  warnings: Array<string>;
  tableFulfillment?: TableFulfillment;
  /**
   * Why the result is not `answered` (Python's `ToolResultReason`, which holds
   * the table reason codes). Recorded only; nothing reads it yet.
   */
  reason?: string;
}

/**
 * What ran in one Analyst turn, stage by stage (wave F track G, steps 1 and
 * 2a). Python's `AnalystTurnRecord` (models/report_analyst.py) owns the
 * shape; Nest stores it in the run's `analysis_plan` column and never reads
 * it. `plan` is the typed turn plan the turn ran on (the only owner of the
 * route); `planFailure` is why a turn had none.
 */
export interface AnalystTurnRecord {
  schemaVersion: "analyst-turn-record.v2";
  exit: string;
  gate: {
    action: AnalystGateAction;
    reasonCode: string;
    source: string;
  } | null;
  plan: {
    source: "llm";
    route: string;
    cannotAnswer: Array<string>;
    questionIds: Array<string>;
    groupings: Array<{ axisKind: string; axisId: string; levels: Array<string> }>;
    evidenceNeeds: Array<string>;
    artifactKind: "none" | "table" | "chart";
    tableKind: string | null;
  } | null;
  planFailure: {
    code: "planner_unavailable" | "plan_invalid";
    issues: Array<string>;
  } | null;
  tools: Array<{
    name: string;
    args: Record<string, unknown>;
    origin: "agent" | "code";
    answerability: AnalystAnswerability;
    reason: string | null;
    factCount: number;
    tableKinds: Array<string | null>;
    chartCount: number;
  }>;
  synthesis: {
    outcome: "model" | "salvaged" | "template";
    template: string | null;
    auditIssues: Array<string>;
    answerKind: "result" | "evidence_gap" | "method_gap" | null;
    planAnswerKind: "result" | "evidence_gap" | "method_gap" | null;
    answerKindConflict: boolean;
    droppedFindings: number;
  } | null;
  relevance: Array<{ target: string; verdict: string; source: string }>;
  artifacts: { tableKinds: Array<string | null>; chartTypes: Array<string> };
  /** The reply language (the report's) and every template copy without it. */
  replyLanguage: string | null;
  copyMissing: Array<{ copyId: string; language: string }>;
}

export interface PersistedAnalystMessage {
  token: string;
  requestToken: string | null;
  clientMessageToken: string | null;
  role: AnalystMessageRole;
  status: AnalystAsyncStatus;
  content: string;
  /** The suggestion chip a user message was sent from; null for typed messages and every assistant message. */
  suggestionId: AnalystSuggestionId | null;
  evidence: Array<EvidenceFact>;
  charts: Array<AnalystChartSpec>;
  tables: Array<AnalystTableSpec>;
  errorMessage: string | null;
  /** The reply's kind; null for user messages and turns still running. */
  responseKind: AnalystResponseKind | null;
  /** Why a failed turn failed; null unless `status` is `failed`. */
  failureCode: AnalystFailureCode | null;
  /** The reply's caveat lines, kept out of `content`. */
  caveats: Array<string>;
  createdAt: string;
  updatedAt: string;
}

export interface PersistedAnalystSession {
  token: string;
  experimentToken: string;
  messages: Array<PersistedAnalystMessage>;
  /**
   * The storyline revision the thread started on (its first message); null
   * for an empty thread, one started before storylines, or a report without
   * one. The analyst tab compares it with the current storyline's revision.
   */
  storylineRevision: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitAnalystMessageRequest {
  requestToken: string;
  clientMessageToken: string;
  content: string;
  /** The suggestion chip the message was sent from; absent for a typed message. */
  suggestionId?: AnalystSuggestionId;
  /** The viewer's UI language (BCP 47): the analyst plot chrome is drawn in it. */
  uiLanguage?: string;
}

export interface AnalystRunResponse {
  token: string;
  requestToken: string;
  status: AnalystAsyncStatus;
  result: AnalysisToolResult | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
}

export interface SubmitAnalystMessageResponse {
  sessionToken: string;
  userMessage: PersistedAnalystMessage;
  assistantMessage: PersistedAnalystMessage | null;
  run: AnalystRunResponse | null;
}

export interface AnalystSessionResponse {
  session: PersistedAnalystSession;
  activeRuns: Array<AnalystRunResponse>;
}

/**
 * Socket ping on `experiment:{experimentToken}:report-analyst`.
 * The HTTP session/run read is the answer; this only says a run changed.
 */
export interface ReportAnalystRunUpdatedPayload {
  experimentToken: string;
  runToken: string;
  status: AnalystAsyncStatus;
  timestamp: string;
}
